//UseDataHooks.js - FIXED VERSION (Remove data transformation)
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api } from "../../api"; 
import { 
  fetchBusinessByEmail,
  fetchStoreIdsByBusinessId,
  fetchSiteNameByStoreId,
  fetchDeckByStoreId,
  fetchSlidesByDeckId,
  fetchDataBySiteId,
  calculateBestPeriods,
  filterDataByDateRange,
  aggregateRatingsData, 
  aggregateYesNoData,
  // Keep these for backward compatibility if needed
  trackSiteSelection,
  clearSiteDataCache,
  fetchDataBySiteIdSmart,
  withErrorHandling,
  fetchData
} from '../../routes/DataView/GraphUtilities';

export const useBusinessData = (userEmail) => {
    const [businessState, setBusinessState] = useState({
      businessId: null,
      businessName: '',
      businessIds: []
    });
    
    useEffect(() => {
      const fetchInitialData = async () => {
        try {
          const business_ids = await fetchBusinessByEmail(userEmail);
          
          if (business_ids && business_ids.length > 0) {
            setBusinessState({
              businessId: business_ids[0]?.business_id,
              businessName: business_ids[0]?.business_name || '',
              businessIds: business_ids
            });
          }
        } catch (error) {
          console.error('Error during initial data fetch:', error);
        }
      };
      
      fetchInitialData();
    }, [userEmail]);
    
    return businessState;
  };
  
// UPDATED useStoreData with StoresData Integration
export const useStoreData = (businessId, enableSmartLoading = true, userId = 'default') => {
  const [storeState, setStoreState] = useState({
    storeIds: [],
    selectedStoreId: null,
    sitePairings: {},
    isDeviceIdsLoading: true,
    connectionError: false,
    connectionErrorMessage: '',
    retryCount: 0,
    storesWithData: {},
    rawStoresData: [],
    // Smart loading state
    isSmartLoading: enableSmartLoading,
    loadingStage: 'scanning',
    sitesChecked: 0,
    smartLoadingStrategy: null
  });
  
  // NEW: Fetch stores using StoresData API
  const fetchStoresWithAvailability = useCallback(async () => {
    const startTime = performance.now();
    console.log('fetchStoresWithAvailability started for business:', businessId);
    
    setStoreState(prev => ({ 
      ...prev, 
      isDeviceIdsLoading: true, 
      connectionError: false,
      isSmartLoading: enableSmartLoading,
      loadingStage: 'scanning',
      sitesChecked: 0
    }));
    
    try {
      console.log('Making API call to stores-with-availability...');
      const apiStartTime = performance.now();
      
      const response = await Promise.race([
        api.get(`store-data/stores-with-availability/${businessId}`),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('API timeout after 5 seconds')), 5000)
        )
      ]);
      
      console.log(`API call completed in ${performance.now() - apiStartTime}ms`);
      
      const storesData = response.data;
      if (!storesData || !Array.isArray(storesData)) {
        throw new Error('Invalid stores data format');
      }

      console.log('Processing stores data...', storesData.length, 'stores');

      // Extract store IDs and create pairings
      const allStoreIds = storesData.map(store => store.store_id);
      const pairings = {};
      const initialStoresWithData = {};
      
      if (enableSmartLoading) {
        setStoreState(prev => ({ 
          ...prev, 
          loadingStage: 'loading',
          sitesChecked: allStoreIds.length
        }));
      }
      
      storesData.forEach(store => {
        // Create site pairings (ID -> Name mapping)
        pairings[store.store_id] = store.store_name || `Site ${store.store_id}`;
        
        // Track stores with data based on StoresData flags
        const hasData = store.has_data_today || 
                       store.has_data_this_week || 
                       store.has_recent_data ||
                       (store.computed_quality_score && store.computed_quality_score > 0.3);
        
        initialStoresWithData[store.store_id] = hasData;
      });
      
      // Smart site selection using pre-computed data
      let selectedSite = '';
      let strategy = '';

      if (storesData.length > 0) {
        // Try to get API recommendation first
        try {
          const recommendationResponse = await api.get(`/store-data/recommended-store/${businessId}`);
          const recommendation = recommendationResponse.data;
          
          if (recommendation.recommended_store_id && allStoreIds.includes(recommendation.recommended_store_id)) {
            selectedSite = recommendation.recommended_store_id;
            strategy = `api_recommendation_${recommendation.reason}`;
            console.log('Using API-recommended site:', recommendation.recommended_store_id, 'reason:', recommendation.reason);
          }
        } catch (error) {
          console.log('Could not get API recommendation, falling back to local selection');
        }

        // Fallback to local selection if no API recommendation
        if (!selectedSite) {
          // Sort stores by priority: data today > quality score > responses today
          const sortedStores = [...storesData].sort((a, b) => {
            // Primary: has data today
            if (a.has_data_today !== b.has_data_today) {
              return b.has_data_today - a.has_data_today;
            }
            // Secondary: computed quality score
            if (a.computed_quality_score !== b.computed_quality_score) {
              return (b.computed_quality_score || 0) - (a.computed_quality_score || 0);
            }
            // Tertiary: responses today
            return (b.responses_today || 0) - (a.responses_today || 0);
          });

          const bestStore = sortedStores[0];
          selectedSite = bestStore.store_id;

          if (bestStore.has_data_today) {
            strategy = bestStore.responses_today > 10 ? 'high_volume_today' : 'data_today';
          } else if (bestStore.has_data_this_week) {
            strategy = 'data_this_week';
          } else if (bestStore.computed_quality_score > 0.5) {
            strategy = 'best_quality';
          } else {
            strategy = 'first_available';
          }

          console.log('Selected site using local logic:', selectedSite, 'strategy:', strategy);
        }
      }

      // Track selection for preference learning
      if (selectedSite) {
        localStorage.setItem('selectedStoreId', selectedSite);
        trackSiteSelection(userId, selectedSite);
      }

      // Update state with all the data INCLUDING rawStoresData
      setStoreState(prev => ({
        ...prev,
        storeIds: allStoreIds,
        selectedStoreId: selectedSite,
        sitePairings: pairings,
        storesWithData: initialStoresWithData,
        smartLoadingStrategy: strategy,
        rawStoresData: storesData
      }));

      // Remove the artificial delays for instant loading
      if (enableSmartLoading) {
        setStoreState(prev => ({ 
          ...prev, 
          loadingStage: 'ready',
          isSmartLoading: false,
          isDeviceIdsLoading: false,
          retryCount: 0
        }));
      } else {
        setStoreState(prev => ({
          ...prev,
          isSmartLoading: false,
          isDeviceIdsLoading: false,
          retryCount: 0
        }));
      }

      console.log(`fetchStoresWithAvailability completed in ${performance.now() - startTime}ms`);
      
    } catch (error) {
      console.error(`fetchStoresWithAvailability failed after ${performance.now() - startTime}ms:`, error);
      
      // CRITICAL: Don't get stuck - fallback immediately
      console.log('Falling back to basic store fetching...');
      return fetchStoreIds();
    }
  }, [businessId, enableSmartLoading, userId]);

  // Original fetch method as fallback
  const fetchStoreIds = useCallback(async () => {
    setStoreState(prev => ({ ...prev, isDeviceIdsLoading: true, connectionError: false }));
    
    try {
      console.log('Fetching store_ids for business_id:', businessId);
      const res = await fetchStoreIdsByBusinessId(businessId);
      const ids = res.map(row => row.store_id);
      
      setStoreState(prev => ({
        ...prev,
        storeIds: ids,
        selectedStoreId: ids.length > 0 ? ids[0] : null,
        isDeviceIdsLoading: false,
        retryCount: 0
      }));
    } catch (error) {
      console.error('Error fetching device IDs:', error);
      
      setStoreState(prev => ({
        ...prev,
        isDeviceIdsLoading: false,
        connectionError: true,
        connectionErrorMessage: error.code === 'ETIMEDOUT' 
          ? 'Connection timed out. The database might be temporarily unavailable.'
          : `Could not connect to the database: ${error.message}`,
        retryCount: prev.retryCount + 1
      }));
    }
  }, [businessId]);
  
  // Fetch site pairings (names) - Only for fallback method
  useEffect(() => {
    async function fetchSitePairings() {
      // Skip if we already have pairings from StoresData API
      if (Object.keys(storeState.sitePairings).length > 0) {
        return;
      }

      const pairings = {};
      for (const storeId of storeState.storeIds) {
        try {
          const response = await fetchSiteNameByStoreId(storeId);
          pairings[storeId] = response[0]?.name || `Site ${storeId}`;
        } catch (error) {
          console.error('Error fetching site name:', error);
          pairings[storeId] = `Site ${storeId}`;
        }
      }
      
      setStoreState(prev => ({
        ...prev,
        sitePairings: pairings
      }));
    }
    
    if (storeState.storeIds.length > 0 && !storeState.isSmartLoading) {
      fetchSitePairings();
    }
  }, [storeState.storeIds, storeState.isSmartLoading, storeState.sitePairings]);
  
  // Initial fetch of store IDs
  useEffect(() => {
    if (businessId) {
      const startTime = performance.now();
      console.log('Starting store data fetch for business:', businessId);
      
      fetchStoresWithAvailability()
        .then(() => {
          console.log(`Store data loaded in ${performance.now() - startTime}ms`);
        })
        .catch(error => {
          console.error(`Store data failed after ${performance.now() - startTime}ms:`, error);
        });
    }
  }, [businessId, fetchStoresWithAvailability]);
  
  const setSelectedStoreId = useCallback((storeId) => {
    if (storeId !== storeState.selectedStoreId) {
      setStoreState(prev => ({
        ...prev,
        selectedStoreId: storeId
      }));
      
      // Track user selection for preference learning
      if (enableSmartLoading) {
        trackSiteSelection(userId, storeId);
      }
    }
  }, [storeState.selectedStoreId, enableSmartLoading, userId]);
  
  const updateStoresWithData = useCallback((storeId, hasData) => {
    setStoreState(prev => ({
      ...prev,
      storesWithData: {
        ...prev.storesWithData,
        [storeId]: hasData
      }
    }));
  }, []);

  // Function to trigger real-time update for specific store
  const updateSpecificStore = useCallback(async (storeId) => {
    try {
      console.log('Triggering real-time update for store:', storeId);
      await api.post('store-data/update-specific-store', { store_id: storeId });
      
      // Refresh the data after update
      fetchStoresWithAvailability();
      
      console.log('Real-time update completed for store:', storeId);
    } catch (error) {
      console.error('Failed to update specific store:', error);
    }
  }, [fetchStoresWithAvailability]);
  
  return {
    ...storeState,
    rawStoresData: storeState.rawStoresData || [],
    setSelectedStoreId,
    updateStoresWithData,
    updateSpecificStore,
    fetchStoreIds: fetchStoresWithAvailability
  };
};

// Rest of your hooks remain the same...
export const useDeckData = (selectedStoreId, storeIds, updateStoresWithData, effectiveDeckId = null) => {
  const [deckState, setDeckState] = useState({
    currentDeck: null,
    isDataLoading: false,
    error: null,
    slideTitles: {
      question1Title: 'Question 1',
      question2Title: 'Question 2',
      question3Title: 'Question 3'
    },
    slideCount: 0
  });
  
  // Fetch deck ID for the selected store
  useEffect(() => {
    const fetchDeckId = async () => {
      if (!selectedStoreId) return;
      
      try {
        setDeckState(prev => ({ ...prev, isDataLoading: true }));
        const deckId = await fetchDeckByStoreId(selectedStoreId);
        
        if (deckId) {
          setDeckState(prev => ({
            ...prev,
            currentDeck: deckId,
            isDataLoading: false
          }));
          
          // Mark store as having data
          updateStoresWithData(selectedStoreId, true);
        } else {
          // Mark store as not having data
          updateStoresWithData(selectedStoreId, false);
          
          // Find current index in storeIds array
          const currentIndex = storeIds.indexOf(selectedStoreId);
          
          if (currentIndex >= 0 && currentIndex < storeIds.length - 1) {
            // Logic to move to next store would be handled by parent component
          } else if (storeIds.length > 0 && currentIndex === storeIds.length - 1) {
            setDeckState(prev => ({
              ...prev,
              error: 'No decks found for any available sites',
              isDataLoading: false
            }));
          } else {
            setDeckState(prev => ({
              ...prev,
              error: 'No deck found for this site',
              isDataLoading: false
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching deck ID:', error);
        setDeckState(prev => ({
          ...prev,
          error: 'Error fetching deck data',
          isDataLoading: false
        }));
      }
    };

    if (selectedStoreId) {
      fetchDeckId();
    }
  }, [selectedStoreId, storeIds, updateStoresWithData]);
  
  // Fetch slide titles when deck ID is available
  useEffect(() => {
    const loadDeckSlides = async () => {
      // Handle "All Sites" case - use generic titles
      if (selectedStoreId === 'all') {
        console.log('📊 Using generic titles for "All Sites" view');
        setDeckState(prev => ({
          ...prev,
          slideTitles: {
            question1Title: 'All Score Data - Question 1',
            question2Title: 'All Score Data - Question 2',
            question3Title: 'All Score Data - Question 3'
          },
          slideCount: 3
        }));
        return;
      }

      // Use effectiveDeckId if provided (user-selected deck), otherwise use currentDeck (store's assigned deck)
      const deckIdToUse = effectiveDeckId || deckState.currentDeck;

      if (deckIdToUse) {
        try {
          console.log('🎴 Fetching slides for deck:', deckIdToUse);
          const slidesData = await fetchSlidesByDeckId(deckIdToUse);

          const updatedTitles = { ...deckState.slideTitles };

          // Set default slide count to the actual number of slides
          let numberOfSlides = slidesData.length;

          slidesData.forEach(slide => {
            if (slide.slide_number === 1) {
              updatedTitles.question1Title = slide.title || 'Question 1';
            }
            if (slide.slide_number === 2) {
              updatedTitles.question2Title = slide.title || 'Question 2';
            }
            if (slide.slide_number === 3) {
              updatedTitles.question3Title = slide.title || 'Question 3';
            }
          });

          setDeckState(prev => ({
            ...prev,
            slideTitles: updatedTitles,
            slideCount: numberOfSlides
          }));
        } catch (error) {
          console.error('Error fetching deck slides:', error);
        }
      }
    };

    loadDeckSlides();
  }, [effectiveDeckId, deckState.currentDeck, selectedStoreId])
  
  return deckState;
};

export const useChartData = (selectedStoreId, currentDeck, externalConnectionError, retryCount, updateStoresWithData, enableSmartLoading = true, businessId = null, selectedDeviceId = 'all') => {
  const [chartState, setChartState] = useState({
    question1Data: [],
    question2Data: [],
    question3Data: [],
    question4Data: [],
    // Add new state for aggregated data
    aggregatedQuestion1Data: [],
    aggregatedQuestion2Data: [],
    aggregatedQuestion3Data: [],
    isDataLoading: false,
    error: null,
    happyPeriodStart: null,
    happyPeriodEnd: null,
    unhappyPeriodStart: null,
    unhappyPeriodEnd: null,
    connectionError: false,
    connectionErrorMessage: '',
    dateRange: 'today',
    chartType: 'bar'
  });

  useEffect(() => {
  // Clear chart data when selectedStoreId is cleared (business change)
  if (!selectedStoreId) {
    setChartState(prev => ({
      ...prev,
      question1Data: [],
      question2Data: [],
      question3Data: [],
      question4Data: [],
      aggregatedQuestion1Data: [],
      aggregatedQuestion2Data: [],
      aggregatedQuestion3Data: [],
      happyPeriodStart: null,
      happyPeriodEnd: null,
      unhappyPeriodStart: null,
      unhappyPeriodEnd: null,
      error: null,
      connectionError: false,
      connectionErrorMessage: ''
    }));
  }
}, [selectedStoreId]);
  
  // Fetch chart data when selectedStoreId and currentDeck are available
  useEffect(() => {
    const loadData = async () => {
  if (selectedStoreId && currentDeck) {
    try {
      setChartState(prev => ({ 
        ...prev, 
        isDataLoading: true,
        connectionError: false 
      }));
      
      // FIX: Handle the case where fetchDataBySiteIdSmart doesn't return an array
      const result = await fetchDataBySiteIdSmart(
        selectedStoreId,
        currentDeck,
        enableSmartLoading,
        businessId  // Pass businessId for "All Sites" aggregation
      );
      
      // Ensure we have a proper array response
      let graph1data, graph2data, graph3data, graph4data;
      
      if (Array.isArray(result) && result.length >= 4) {
        [graph1data, graph2data, graph3data, graph4data] = result;
      } else if (result && typeof result === 'object') {
        // Handle case where result is an object with named properties
        graph1data = result.graph1data || result.question1Data || [];
        graph2data = result.graph2data || result.question2Data || [];
        graph3data = result.graph3data || result.question3Data || [];
        graph4data = result.graph4data || result.question4Data || [];
      } else {
        // Fallback: initialize empty arrays
        console.warn('fetchDataBySiteIdSmart returned unexpected format:', result);
        graph1data = [];
        graph2data = [];
        graph3data = [];
        graph4data = [];
      }

      // Filter by device if a specific device is selected
      if (selectedDeviceId && selectedDeviceId !== 'all') {
        console.log(`🎯 Filtering data for device: ${selectedDeviceId}`);
        graph1data = (graph1data || []).filter(item => item.device_id === selectedDeviceId);
        graph2data = (graph2data || []).filter(item => item.device_id === selectedDeviceId);
        graph3data = (graph3data || []).filter(item => item.device_id === selectedDeviceId);
        graph4data = (graph4data || []).filter(item => item.device_id === selectedDeviceId);
      }

      // Check if we have any actual data
      const hasData =
        (graph1data && graph1data.length > 0) ||
        (graph2data && graph2data.length > 0) ||
        (graph3data && graph3data.length > 0) ||
        (graph4data && graph4data.length > 0);

      console.log(`Chart data loaded for site ${selectedStoreId}:`, {
        q1_points: graph1data?.length || 0,
        q2_points: graph2data?.length || 0,
        q3_points: graph3data?.length || 0,
        q4_points: graph4data?.length || 0,
        device_filter: selectedDeviceId !== 'all' ? selectedDeviceId : 'none',
        has_data: hasData,
        result_type: typeof result,
        result_is_array: Array.isArray(result)
      });

      // Update store data status
      updateStoresWithData(selectedStoreId, hasData);
      
      // Aggregate data for each chart
      const aggregated1 = aggregateRatingsData(graph1data || []);
      const aggregated2 = aggregateYesNoData(graph2data || []);
      const aggregated3 = aggregateYesNoData(graph3data || []);
      
      setChartState(prev => ({
        ...prev,
        question1Data: graph1data || [],
        question2Data: graph2data || [],
        question3Data: graph3data || [],
        question4Data: graph4data || [],
        aggregatedQuestion1Data: aggregated1,
        aggregatedQuestion2Data: aggregated2,
        aggregatedQuestion3Data: aggregated3,
        isDataLoading: false
      }));
    } catch (error) {
      console.error('Error loading data:', error);
      
      // Mark this store as having an error
      updateStoresWithData(selectedStoreId, false);
      
      setChartState(prev => ({
        ...prev,
        isDataLoading: false,
        connectionError: true,
        connectionErrorMessage: error.code === 'ETIMEDOUT' 
          ? 'Connection timed out while fetching chart data.'
          : `Error loading chart data: ${error.message}`
      }));
    }
  }
};

    if (selectedStoreId && currentDeck && !externalConnectionError) {
      loadData();
    }
  }, [selectedStoreId, currentDeck, externalConnectionError, retryCount, updateStoresWithData, enableSmartLoading, selectedDeviceId]);
  
  // Calculate best periods for the chart when data changes
  useEffect(() => {
      if (chartState.question4Data.length > 0) {
        try {
          const [{happyPeriodStart, happyPeriodEnd}, {unhappyPeriodStart, unhappyPeriodEnd}] = 
            calculateBestPeriods(chartState.question4Data, chartState.dateRange);
          
          if (chartState.dateRange === 'today' || chartState.dateRange === 'yesterday') {
            setChartState(prev => ({
              ...prev,
              happyPeriodStart,
              happyPeriodEnd,
              unhappyPeriodStart,
              unhappyPeriodEnd
            }));
          } else {
            const formatDateTime = (date) => {
              if (date === null) return null;
              return date instanceof Date ? date.toISOString().split('T')[0] : date;
            };
            
            setChartState(prev => ({
              ...prev,
              happyPeriodStart: formatDateTime(happyPeriodStart),
              happyPeriodEnd: formatDateTime(happyPeriodEnd),
              unhappyPeriodStart: formatDateTime(unhappyPeriodStart),
              unhappyPeriodEnd: formatDateTime(unhappyPeriodEnd)
            }));
          }
        } catch (error) {
          console.error('Error calculating best periods:', error);
        }
      }
    }, [chartState.question4Data, chartState.dateRange]);
    
  // Helper to change date range
  const setDateRange = useCallback((newDateRange) => {
    setChartState(prev => ({
      ...prev,
      dateRange: newDateRange
    }));
  }, []);
  
  // Helper to change chart type
  const setChartType = useCallback((newChartType) => {
    setChartState(prev => ({ 
      ...prev,
      chartType: newChartType
    }));
  }, []);
  
  // Generate filtered data without timezone conversion
  const getFilteredData = useCallback(() => {
    console.log('getFilteredData called with dateRange:', chartState.dateRange);
    console.log('Raw question4Data length:', chartState.question4Data?.length || 0);
    
    // Filter by date range and sort by timestamp
    const filteredByDate = {
      question1Data: chartState.question1Data.length > 0 
        ? filterDataByDateRange(chartState.question1Data, chartState.dateRange)
          .sort((a, b) => new Date(a.feedback_time || a.date) - new Date(b.feedback_time || b.date))
        : [],
      question2Data: chartState.question2Data.length > 0 
        ? filterDataByDateRange(chartState.question2Data, chartState.dateRange)
          .sort((a, b) => new Date(a.feedback_time || a.date) - new Date(b.feedback_time || b.date))
        : [],
      question3Data: chartState.question3Data.length > 0 
        ? filterDataByDateRange(chartState.question3Data, chartState.dateRange)
          .sort((a, b) => new Date(a.feedback_time || a.date) - new Date(b.feedback_time || b.date))
        : [],
      question4Data: chartState.question4Data.length > 0 
        ? filterDataByDateRange(chartState.question4Data, chartState.dateRange)
          .sort((a, b) => new Date(a.feedback_time || a.date) - new Date(b.feedback_time || b.date))
        : []
    };
    
    console.log('After date filtering - question4Data length:', filteredByDate.question4Data?.length || 0);
    console.log('Filtered question4Data sample:', filteredByDate.question4Data?.[0]);
    
    // Return processed data - ChartOptions will handle the aggregation and display formatting
    return {
      // Aggregate filtered data for first three questions
      question1Data: aggregateRatingsData(filteredByDate.question1Data),
      question2Data: aggregateYesNoData(filteredByDate.question2Data),
      question3Data: aggregateYesNoData(filteredByDate.question3Data),
      // Pass filtered data to ChartOptions for processing
      question4Data: filteredByDate.question4Data
    };
  }, [chartState.question1Data, chartState.question2Data, chartState.question3Data, chartState.question4Data, chartState.dateRange]);
  
  // Generate available data for each date range option (for chart 4)
  const getAvailableData = useCallback(() => {
    return {
      today: chartState.question4Data.length > 0 ? filterDataByDateRange(chartState.question4Data, 'today') : [],
      yesterday: chartState.question4Data.length > 0 ? filterDataByDateRange(chartState.question4Data, 'yesterday') : [],
      week: chartState.question4Data.length > 0 ? filterDataByDateRange(chartState.question4Data, 'week') : [],
      month: chartState.question4Data.length > 0 ? filterDataByDateRange(chartState.question4Data, 'month') : [],
      year: chartState.question4Data.length > 0 ? filterDataByDateRange(chartState.question4Data, 'year') : [],
      all: chartState.question4Data
    };
  }, [chartState.question4Data]);
  
  // Get all filtered data
  const filteredChartData = getFilteredData();
  
  return {
    ...chartState,
    setDateRange,
    setChartType,
    // Return the aggregated and filtered data for each chart
    filteredQuestion1Data: filteredChartData.question1Data,
    filteredQuestion2Data: filteredChartData.question2Data,
    filteredQuestion3Data: filteredChartData.question3Data,
    filteredData: filteredChartData.question4Data,
    availableData: getAvailableData()
  };
};

// NEW: QR Scan Metrics Hook
export const useQRScanMetrics = (selectedStoreId, dateRange = 'today', refreshInterval = 30000) => {
  const [qrState, setQrState] = useState({
    scans: 0,
    conversionRate: 0,
    isLoading: true,
    error: null,
    lastUpdated: null,
    dateRange: dateRange
  });

  const fetchQRMetrics = useCallback(async () => {
    if (!selectedStoreId) {
      console.log('🚫 useQRScanMetrics: No selectedStoreId provided');
      return;
    }
    
    try {
      console.log('🔄 Fetching QR scan metrics with params:', {
        selectedStoreId,
        dateRange,
        timestamp: new Date().toISOString()
      });

      setQrState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Pass dateRange as query parameter
      const url = `/complaints-data/qr-scan-metrics/${selectedStoreId}?dateRange=${dateRange}`;
      console.log('📡 Making API call to:', url);
      
      const response = await api.get(url);
      const data = response.data;
      
      console.log('✅ QR metrics API response:', data);
      
      setQrState(prev => ({
        ...prev,
        scans: data.scans || 0,
        conversionRate: data.conversionRate || 0,
        isLoading: false,
        lastUpdated: new Date().toISOString(),
        dateRange: data.dateRange || dateRange
      }));

      console.log('📊 QR metrics state updated for', dateRange, ':', {
        scans: data.scans || 0,
        conversionRate: data.conversionRate || 0,
        dateRange: data.dateRange || dateRange
      });
      
    } catch (error) {
      console.error('❌ Error fetching QR scan metrics:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      setQrState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch QR metrics'
      }));
    }
  }, [selectedStoreId, dateRange]);

  // Fetch data on mount and when selectedStoreId or dateRange changes
  useEffect(() => {
    console.log('🔧 useQRScanMetrics useEffect triggered:', {
      selectedStoreId,
      dateRange,
      timestamp: new Date().toISOString()
    });
    
    if (selectedStoreId) {
      fetchQRMetrics();
    }
  }, [selectedStoreId, dateRange, fetchQRMetrics]);

  // Set up auto-refresh
  useEffect(() => {
    if (!selectedStoreId || refreshInterval <= 0) return;

    console.log('⏰ Setting up QR metrics auto-refresh every', refreshInterval, 'ms');
    const interval = setInterval(fetchQRMetrics, refreshInterval);
    return () => {
      console.log('🧹 Cleaning up QR metrics interval');
      clearInterval(interval);
    };
  }, [selectedStoreId, refreshInterval, fetchQRMetrics]);

  return {
    ...qrState,
    refetch: fetchQRMetrics
  };
};

// Updated useComplaintsData with debug logging
export const useComplaintsData = (selectedStoreId, dateRange = 'today', refreshInterval = 30000) => {
  const [complaintsState, setComplaintsState] = useState({
    data: [],
    isLoading: true,
    error: null,
    lastUpdated: null,
    totalComplaints: 0,
    dateRange: dateRange
  });

  const fetchComplaintsData = useCallback(async () => {
    if (!selectedStoreId) {
      console.log('🚫 useComplaintsData: No selectedStoreId provided');
      return;
    }
    
    try {
      console.log('🔄 Fetching complaints data with params:', {
        selectedStoreId,
        dateRange,
        timestamp: new Date().toISOString()
      });

      setComplaintsState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Pass dateRange as query parameter
      const url = `/complaints-data/complaints-data/${selectedStoreId}?dateRange=${dateRange}`;
      console.log('📡 Making API call to:', url);
      
      const response = await api.get(url);
      const data = response.data;
      
      console.log('✅ Complaints API response:', data);
      
      const totalComplaints = Array.isArray(data) ? data.reduce((sum, item) => sum + (item.count || 0), 0) : 0;
      
      setComplaintsState(prev => ({
        ...prev,
        data: data || [],
        totalComplaints,
        isLoading: false,
        lastUpdated: new Date().toISOString(),
        dateRange: dateRange
      }));

      console.log('📋 Complaints state updated for', dateRange, ':', { 
        items: data?.length || 0, 
        total: totalComplaints,
        dateRange: dateRange
      });
      
    } catch (error) {
      console.error('❌ Error fetching complaints data:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      setComplaintsState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch complaints data'
      }));
    }
  }, [selectedStoreId, dateRange]);

  // Fetch data on mount and when selectedStoreId or dateRange changes
  useEffect(() => {
    console.log('🔧 useComplaintsData useEffect triggered:', {
      selectedStoreId,
      dateRange,
      timestamp: new Date().toISOString()
    });
    
    if (selectedStoreId) {
      fetchComplaintsData();
    }
  }, [selectedStoreId, dateRange, fetchComplaintsData]);

  // Set up auto-refresh
  useEffect(() => {
    if (!selectedStoreId || refreshInterval <= 0) return;

    console.log('⏰ Setting up complaints auto-refresh every', refreshInterval, 'ms');
    const interval = setInterval(fetchComplaintsData, refreshInterval);
    return () => {
      console.log('🧹 Cleaning up complaints interval');
      clearInterval(interval);
    };
  }, [selectedStoreId, refreshInterval, fetchComplaintsData]);

  return {
    ...complaintsState,
    refetch: fetchComplaintsData
  };
};

// Updated useDashboardMetrics with debug logging
export const useDashboardMetrics = (selectedStoreId, dateRange = 'today', refreshInterval = 30000) => {
  console.log('🎛️ useDashboardMetrics called with:', {
    selectedStoreId,
    dateRange,
    refreshInterval,
    timestamp: new Date().toISOString()
  });

  const qrMetrics = useQRScanMetrics(selectedStoreId, dateRange, refreshInterval);
  const complaintsData = useComplaintsData(selectedStoreId, dateRange, refreshInterval);

  const [combinedState, setCombinedState] = useState({
    isLoading: true,
    hasError: false,
    errorMessage: '',
    lastUpdated: null
  });

  // Update combined state when individual hooks change
  useEffect(() => {
    const isLoading = qrMetrics.isLoading || complaintsData.isLoading;
    const hasError = !!qrMetrics.error || !!complaintsData.error;
    const errorMessage = qrMetrics.error || complaintsData.error || '';
    const lastUpdated = qrMetrics.lastUpdated || complaintsData.lastUpdated;

    console.log('🔄 Combined state updating:', {
      isLoading,
      hasError,
      errorMessage,
      lastUpdated,
      qrScans: qrMetrics.scans,
      complaintsTotal: complaintsData.totalComplaints,
      dateRange: dateRange
    });

    setCombinedState({
      isLoading,
      hasError,
      errorMessage,
      lastUpdated
    });
  }, [qrMetrics.isLoading, qrMetrics.error, qrMetrics.lastUpdated, qrMetrics.scans,
      complaintsData.isLoading, complaintsData.error, complaintsData.lastUpdated, complaintsData.totalComplaints, dateRange]);

  const refetchAll = useCallback(async () => {
    console.log('🔄 Refreshing all dashboard metrics for dateRange:', dateRange);
    await Promise.all([
      qrMetrics.refetch(),
      complaintsData.refetch()
    ]);
  }, [qrMetrics.refetch, complaintsData.refetch, dateRange]);

  return {
    qrMetrics: {
      scans: qrMetrics.scans,
      conversionRate: qrMetrics.conversionRate,
      isLoading: qrMetrics.isLoading,
      error: qrMetrics.error,
      dateRange: qrMetrics.dateRange
    },
    complaintsData: {
      data: complaintsData.data,
      totalComplaints: complaintsData.totalComplaints,
      isLoading: complaintsData.isLoading,
      error: complaintsData.error,
      dateRange: complaintsData.dateRange
    },
    combined: combinedState,
    refetchAll
  };
};

export const useAnalytics = (selectedStoreId, businessId, enableSmartLoading = true) => {
  const [analyticsState, setAnalyticsState] = useState({
    insights: [],
    trends: {},
    recommendations: [],
    isAnalyzing: false,
    lastAnalyzed: null
  });

  // Memoize the generateInsights function with proper dependencies
  const generateInsights = useCallback(async (qrData, complaintsData, chartData) => {
    try {
      setAnalyticsState(prev => ({ ...prev, isAnalyzing: true }));

      // Generate basic insights
      const insights = [];
      const recommendations = [];

      // QR Code insights
      if (qrData && qrData.scans > 0) {
        if (qrData.conversionRate > 5) {
          insights.push({
            type: 'positive',
            category: 'qr_performance',
            text: `Strong QR code engagement with ${qrData.conversionRate.toFixed(1)}% conversion rate`,
            priority: 'high'
          });
        } else if (qrData.conversionRate < 2) {
          insights.push({
            type: 'warning',
            category: 'qr_performance',
            text: `Low QR code conversion rate at ${qrData.conversionRate.toFixed(1)}%`,
            priority: 'medium'
          });
          recommendations.push({
            text: 'Consider improving QR code placement or adding incentives',
            category: 'qr_optimization'
          });
        }
      }

      // Complaints insights
      if (complaintsData && complaintsData.totalComplaints > 0 && complaintsData.data) {
        const topComplaint = complaintsData.data[0];
        if (topComplaint) {
          insights.push({
            type: 'info',
            category: 'complaints',
            text: `Most common complaint: ${topComplaint.text} (${topComplaint.count} reports)`,
            priority: 'medium'
          });

          if (topComplaint.count > complaintsData.totalComplaints * 0.4) {
            recommendations.push({
              text: `Focus on addressing ${topComplaint.text.toLowerCase()} issues`,
              category: 'complaint_resolution'
            });
          }
        }
      }

      setAnalyticsState(prev => ({
        ...prev,
        insights,
        recommendations,
        isAnalyzing: false,
        lastAnalyzed: new Date().toISOString()
      }));

    } catch (error) {
      console.error('Error generating insights:', error);
      setAnalyticsState(prev => ({
        ...prev,
        isAnalyzing: false
      }));
    }
  }, [selectedStoreId, businessId]);

  return {
    ...analyticsState,
    generateInsights
  };
};

export const useSiteHealthScore = (selectedStoreId, dateRange = 'today', refreshInterval = 30000, businessId = null) => {
  const [healthState, setHealthState] = useState({
    score: 0,
    trends: { lastWeek: 0, lastMonth: 0 },
    isLoading: true,
    error: null,
    lastUpdated: null,
    dateRange: dateRange
  });

  const fetchSiteHealthData = useCallback(async () => {
    if (!selectedStoreId) {
      console.log('🚫 useSiteHealthScore: No selectedStoreId provided');
      return;
    }
    
    try {
      console.log('🔄 Fetching site health data with params:', {
        selectedStoreId,
        dateRange,
        businessId: selectedStoreId === 'all' ? businessId : 'N/A (single site)',
        mode: selectedStoreId === 'all' ? 'ALL SITES AGGREGATION' : 'SINGLE SITE',
        timestamp: new Date().toISOString()
      });

      setHealthState(prev => ({ ...prev, isLoading: true, error: null }));
      
      // Fetch all feedback data with date range filtering
      const baseUrl = `/analytics/v2`;

      // Build query params - add business_id when site_id is 'all'
      const buildQueryParams = (questionNum) => {
        let params = `site_id=${selectedStoreId}&dateRange=${dateRange}`;
        if (selectedStoreId === 'all' && businessId) {
          params += `&business_id=${businessId}`;
        }
        return params;
      };

      const endpoints = [
        `${baseUrl}/question_1?${buildQueryParams(1)}`,
        `${baseUrl}/question_2?${buildQueryParams(2)}`,
        `${baseUrl}/question_3?${buildQueryParams(3)}`
      ];
      
      console.log('📡 Making API calls to:', endpoints);
      
      const responses = await Promise.allSettled(
        endpoints.map(url => api.get(url))
      );
      
      // Combine all successful responses
      const allFeedbackData = responses
        .filter(result => result.status === 'fulfilled')
        .flatMap(result => result.value.data.results || []);
      
      console.log('✅ Site health API responses combined:', {
        totalDataPoints: allFeedbackData.length,
        dateRange: dateRange
      });
      
      // Calculate health score using the existing function but with date-filtered data
      const scoreData = calculateSiteHealthScoreWithDateRange(allFeedbackData, dateRange);
      
      setHealthState(prev => ({
        ...prev,
        score: scoreData.score,
        trends: scoreData.trends,
        isLoading: false,
        lastUpdated: new Date().toISOString(),
        dateRange: dateRange
      }));

      console.log('📊 Site health score calculated for', dateRange, ':', scoreData);
      
    } catch (error) {
      console.error('❌ Error fetching site health data:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      setHealthState(prev => ({
        ...prev,
        isLoading: false,
        error: error.response?.data?.error || error.message || 'Failed to fetch site health data'
      }));
    }
  }, [selectedStoreId, dateRange, businessId]);

  // Fetch data on mount and when selectedStoreId or dateRange changes
  useEffect(() => {
    console.log('🔧 useSiteHealthScore useEffect triggered:', {
      selectedStoreId,
      dateRange,
      timestamp: new Date().toISOString()
    });
    
    if (selectedStoreId) {
      fetchSiteHealthData();
    }
  }, [selectedStoreId, dateRange, fetchSiteHealthData]);

  // Set up auto-refresh
  useEffect(() => {
    if (!selectedStoreId || refreshInterval <= 0) return;

    console.log('⏰ Setting up site health auto-refresh every', refreshInterval, 'ms');
    const interval = setInterval(fetchSiteHealthData, refreshInterval);
    return () => {
      console.log('🧹 Cleaning up site health interval');
      clearInterval(interval);
    };
  }, [selectedStoreId, refreshInterval, fetchSiteHealthData]);

  return {
    ...healthState,
    refetch: fetchSiteHealthData
  };
};

// Updated calculation function that works better with different date ranges
export const calculateSiteHealthScoreWithDateRange = (feedbackData, dateRange) => {
  if (!feedbackData || feedbackData.length === 0) {
    console.log("No feedback data available for health score calculation");
    return { score: 0, trends: { lastWeek: 0, lastMonth: 0 } };
  }

  console.log('Calculating health score for dateRange:', dateRange, 'with', feedbackData.length, 'data points');

  // For different date ranges, we need different comparison logic
  let currentScore = calculatePeriodScore(feedbackData);
  let weekTrend = 0;
  let monthTrend = 0;

  // Calculate trends based on the selected date range
  if (dateRange === 'today') {
    // Compare today vs yesterday
    const now = new Date();
    const startOfToday = new Date(now);
    startOfToday.setHours(0, 0, 0, 0);
    
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    
    const todayData = feedbackData.filter(item => 
      new Date(item.feedback_time) >= startOfToday);
    const yesterdayData = feedbackData.filter(item => 
      new Date(item.feedback_time) >= startOfYesterday && 
      new Date(item.feedback_time) < startOfToday);
    
    const todayScore = calculatePeriodScore(todayData);
    const yesterdayScore = calculatePeriodScore(yesterdayData);
    
    currentScore = todayScore;
    weekTrend = yesterdayScore > 0 ? ((todayScore - yesterdayScore) / yesterdayScore) * 100 : 0;
    monthTrend = 0; // Not applicable for single day
    
  } else if (dateRange === 'week') {
    // Compare this week vs last week
    const now = new Date();
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(now.getDate() - 7);
    
    const twoWeeksAgo = new Date(now);
    twoWeeksAgo.setDate(now.getDate() - 14);
    
    const thisWeekData = feedbackData.filter(item => 
      new Date(item.feedback_time) >= oneWeekAgo);
    const lastWeekData = feedbackData.filter(item => 
      new Date(item.feedback_time) >= twoWeeksAgo && 
      new Date(item.feedback_time) < oneWeekAgo);
    
    const thisWeekScore = calculatePeriodScore(thisWeekData);
    const lastWeekScore = calculatePeriodScore(lastWeekData);
    
    currentScore = thisWeekScore;
    weekTrend = lastWeekScore > 0 ? ((thisWeekScore - lastWeekScore) / lastWeekScore) * 100 : 0;
    
  } else if (dateRange === 'month') {
    // Compare this month vs last month
    const now = new Date();
    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(now.getMonth() - 1);
    
    const twoMonthsAgo = new Date(now);
    twoMonthsAgo.setMonth(now.getMonth() - 2);
    
    const thisMonthData = feedbackData.filter(item => 
      new Date(item.feedback_time) >= oneMonthAgo);
    const lastMonthData = feedbackData.filter(item => 
      new Date(item.feedback_time) >= twoMonthsAgo && 
      new Date(item.feedback_time) < oneMonthAgo);
    
    const thisMonthScore = calculatePeriodScore(thisMonthData);
    const lastMonthScore = calculatePeriodScore(lastMonthData);
    
    currentScore = thisMonthScore;
    monthTrend = lastMonthScore > 0 ? ((thisMonthScore - lastMonthScore) / lastMonthScore) * 100 : 0;
    
  } else {
    // For 'all', 'year', or other ranges, use the original logic but simplified
    const sortedData = [...feedbackData].sort((a, b) => 
      new Date(a.feedback_time) - new Date(b.feedback_time));
    
    if (sortedData.length > 20) { // Need enough data for meaningful comparison
      const halfPoint = Math.floor(sortedData.length / 2);
      const olderHalfScore = calculatePeriodScore(sortedData.slice(0, halfPoint));
      const newerHalfScore = calculatePeriodScore(sortedData.slice(halfPoint));
      
      currentScore = newerHalfScore;
      weekTrend = olderHalfScore > 0 ? ((newerHalfScore - olderHalfScore) / olderHalfScore) * 100 : 0;
      monthTrend = weekTrend; // Use same trend for both
    }
  }

  const result = {
    score: currentScore,
    trends: {
      lastWeek: Math.round(weekTrend * 10) / 10, // Round to 1 decimal
      lastMonth: Math.round(monthTrend * 10) / 10
    }
  };

  console.log('Health score calculation result:', result);
  return result;
};

// Keep the existing calculatePeriodScore function
const calculatePeriodScore = (periodData) => {
  if (!periodData || periodData.length === 0) return 0;
  
  let ratingSum = 0;
  let ratingCount = 0;
  let yesCount = 0;
  let noCount = 0;

  // Process different types of feedback
  periodData.forEach(item => {
    if (item.response_type === 'rating' && item.rating !== null) {
      ratingSum += parseFloat(item.rating);
      ratingCount++;
    } else if (item.response_type === 'yes_no') {
      if (item.yes_no === 'yes') yesCount++;
      else if (item.yes_no === 'no') noCount++;
    }
  });

  // Calculate average rating (scale 1-5)
  const avgRating = ratingCount > 0 ? ratingSum / ratingCount : 0;
  
  // Calculate yes ratio (scale 0-1)
  const yesNoTotal = yesCount + noCount;
  const yesRatio = yesNoTotal > 0 ? yesCount / yesNoTotal : 0;
  
  // Normalize rating to 0-1 scale from 1-5 scale
  const normalizedRating = ratingCount > 0 ? (avgRating - 1) / 4 : 0;
  
  // Weight factors (can be adjusted based on importance)
  const ratingWeight = 0.7;
  const yesNoWeight = 0.3;
  
  // Calculate composite score (0-100 scale)
  let compositeScore = 0;
  
  if (ratingCount > 0 && yesNoTotal > 0) {
    // Both types of feedback exist
    compositeScore = (normalizedRating * ratingWeight + yesRatio * yesNoWeight) * 100;
  } else if (ratingCount > 0) {
    // Only ratings exist
    compositeScore = normalizedRating * 100;
  } else if (yesNoTotal > 0) {
    // Only yes/no exists
    compositeScore = yesRatio * 100;
  }
  
  return Math.round(compositeScore);
};

// NEW: Hook to fetch devices list for a business
export const useDevicesList = (businessId) => {
  const [devicesState, setDevicesState] = useState({
    devices: [],
    deviceOptions: [],
    deviceMapping: {},
    isLoading: true,
    error: null
  });

  useEffect(() => {
    const fetchDevices = async () => {
      if (!businessId) {
        console.log('🚫 useDevicesList: No businessId provided');
        setDevicesState({
          devices: [],
          deviceOptions: [],
          deviceMapping: {},
          isLoading: false,
          error: null
        });
        return;
      }

      try {
        console.log('🔄 Fetching devices list for business:', businessId);
        setDevicesState(prev => ({ ...prev, isLoading: true, error: null }));

        const response = await api.get(`/licensing/${businessId}/devices`);
        const devicesData = response.data || [];

        console.log('✅ Devices data received:', devicesData.length, 'devices');

        // Create options array (device_id values)
        const deviceOptions = devicesData.map(device => device.device_id);

        // Create mapping (device_id -> device_name or store_name)
        const deviceMapping = devicesData.reduce((acc, device) => {
          acc[device.device_id] = device.device_name || device.store_name || `Device ${device.device_id}`;
          return acc;
        }, {});

        setDevicesState({
          devices: devicesData,
          deviceOptions,
          deviceMapping,
          isLoading: false,
          error: null
        });

      } catch (error) {
        console.error('❌ Error fetching devices list:', error);
        setDevicesState({
          devices: [],
          deviceOptions: [],
          deviceMapping: {},
          isLoading: false,
          error: error.response?.data?.error || error.message || 'Failed to fetch devices'
        });
      }
    };

    fetchDevices();
  }, [businessId]);

  return devicesState;
};

// NEW: Hook to fetch decks list for a business
export const useDecksList = (businessId, userEmail) => {
  const [decksState, setDecksState] = useState({
    decks: [],
    deckOptions: [],
    deckMapping: {},
    isLoading: true,
    error: null
  });

  useEffect(() => {
    const fetchDecks = async () => {
      if (!businessId || !userEmail) {
        console.log('🚫 useDecksList: Missing businessId or userEmail');
        setDecksState({
          decks: [],
          deckOptions: [],
          deckMapping: {},
          isLoading: false,
          error: null
        });
        return;
      }

      try {
        console.log('🔄 Fetching decks list for business:', businessId);
        setDecksState(prev => ({ ...prev, isLoading: true, error: null }));

        const response = await api.post('/decks', {
          email: userEmail,
          business_id: businessId
        });
        const decksData = response.data || [];

        console.log('✅ Decks data received:', decksData.length, 'decks');

        // Create options array (deckId values)
        const deckOptions = decksData.map(deck => deck.deckId);

        // Create mapping (deckId -> deckName)
        const deckMapping = decksData.reduce((acc, deck) => {
          acc[deck.deckId] = deck.deckName || `Deck ${deck.deckId}`;
          return acc;
        }, {});

        setDecksState({
          decks: decksData,
          deckOptions,
          deckMapping,
          isLoading: false,
          error: null
        });

      } catch (error) {
        console.error('❌ Error fetching decks list:', error);
        setDecksState({
          decks: [],
          deckOptions: [],
          deckMapping: {},
          isLoading: false,
          error: error.response?.data?.error || error.message || 'Failed to fetch decks'
        });
      }
    };

    fetchDecks();
  }, [businessId, userEmail]);

  return {
    ...decksState,
    // Ensure decks array is always returned
    decks: decksState.decks || []
  };
};
